import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { themeGet, right } from 'styled-system';
import { Flex, Box, Text } from 'roo-ui/components';
import get from 'lodash/get';
import { useSelector } from 'react-redux';
import OfferHighlighter from './OfferHighlighter';
import { mediaQuery } from 'lib/styledSystem';
import OfferPointsEarn from './components/OfferPointsEarn';
import OfferInclusions from './components/OfferInclusions';
import OfferValueAdds from './components/OfferValueAdds';
import OfferPriceBox from './components/OfferPriceBox';
import OfferDetails from './components/OfferDetails';
import { getQueryFeaturedOfferId } from 'store/propertyAvailability/propertyAvailabilitySelectors';
import isNil from 'lodash/isNil';
import usePointsEarned from 'lib/hooks/usePointsEarned/usePointsEarned';

const s4 = themeGet('space.4');
const s5 = themeGet('space.5');
const s8 = themeGet('space.8');
const alto = themeGet('colors.greys.alto');

const Wrapper = styled(Flex)`
  ${mediaQuery.minWidth.sm} {
    padding: 0;
  }
`;

const OfferDetailsWrapper = styled(Box)`
  border-bottom: 1px solid ${alto};
  margin: 0 ${s4};
  padding: ${s4} 0;
  ${mediaQuery.minWidth.sm} {
    width: 30%;
    margin: 0;
    padding: ${s4} ${s8};
    border-bottom: 0;
    border-right: 1px dotted ${alto};
  }
`;

const PointsEarnAndInclusionsWrapper = styled(Box)`
  border-bottom: 1px solid ${alto};
  margin: 0 ${s4};
  padding: ${s4} 0;
  ${mediaQuery.minWidth.sm} {
    padding: ${s8};
    padding-top: ${themeGet('space.10')};
    width: 35%;
    margin: 0;
    border-bottom: 0;
    border-right: 1px solid ${alto};
  }
`;

const PriceBoxWrapper = styled(Box)`
  padding: ${s4};
  ${mediaQuery.minWidth.sm} {
    padding: ${s8};
    width: 38%;
    margin-top: ${({ hasPromo }) => (hasPromo ? s5 : '')};
  }

  ${mediaQuery.minWidth.md} {
    width: 35%;
  }
`;

const Sash = styled(Box)`
  ${right}
`;
Sash.displayName = 'Sash';

const PromotionText = styled(Text)`
  display: inline-block;
  text-transform: uppercase;
  background-color: ${themeGet('colors.brand.secondary')};
  padding: ${themeGet('space.0')} ${themeGet('space.1')};
  margin-left: ${themeGet('space.4')};
  font-size: ${themeGet('fontSizes.xs')};
  vertical-align: top;
  font-weight: ${themeGet('fontWeights.bold')};
`;

const isHighlighted = (featuredId, offerId) => {
  if ([featuredId, offerId].some(isNil)) return false;
  return String(featuredId) === String(offerId);
};

const Offer = ({ roomTypeId, offer, counter, roomName }) => {
  const { id: offerId, name: offerName, charges, inclusions, cancellationPolicy, description, type, depositPay, valueAdds } = offer;
  const promotionName = get(offer, 'promotion.name');
  const hasPromo = !!promotionName;
  const isClassic = offer.type === 'classic';

  const { pointsEarned, onCashPaymentAmountChange } = usePointsEarned({ pointsEarned: offer.pointsEarned });

  const featuredOfferId = useSelector(getQueryFeaturedOfferId);
  const highlighted = isHighlighted(featuredOfferId, offer.id);
  const Highlight = highlighted ? OfferHighlighter : Fragment;

  return (
    <Highlight>
      <Box bg="white" borderRadius="default" boxShadow="hard" position="relative" data-testid="offer-card">
        {promotionName && (
          <Sash position={['initial', 'absolute']} right={['initial', '32px']}>
            <PromotionText>{promotionName}</PromotionText>
          </Sash>
        )}
        <Box mb={4}>
          <Wrapper flexDirection={['column', 'row']}>
            <OfferDetailsWrapper>
              <OfferDetails
                cancellationPolicy={cancellationPolicy}
                offerDescription={description}
                counter={counter}
                offerName={offerName}
                depositPay={depositPay}
              />
            </OfferDetailsWrapper>
            <PointsEarnAndInclusionsWrapper>
              <OfferPointsEarn pointsEarned={pointsEarned} isClassic={isClassic} />
              <OfferInclusions inclusions={inclusions} />
              <OfferValueAdds valueAdds={valueAdds} />
            </PointsEarnAndInclusionsWrapper>
            <PriceBoxWrapper hasPromo={hasPromo}>
              <OfferPriceBox
                charges={charges}
                offerType={type}
                offerName={offerName}
                offerId={offerId}
                roomTypeId={roomTypeId}
                roomName={roomName}
                onCashPaymentAmountChange={onCashPaymentAmountChange}
              />
            </PriceBoxWrapper>
          </Wrapper>
        </Box>
      </Box>
    </Highlight>
  );
};

Offer.propTypes = {
  offer: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    charges: PropTypes.object.isRequired,
    pointsEarned: PropTypes.object.isRequired,
    inclusions: PropTypes.array.isRequired,
    valueAdds: PropTypes.arrayOf(String),
    cancellationPolicy: PropTypes.object.isRequired,
    payableAtProperty: PropTypes.object,
    type: PropTypes.string.isRequired,
    depositPay: PropTypes.object.isRequired,
  }).isRequired,
  roomTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  counter: PropTypes.number.isRequired,
  roomName: PropTypes.string.isRequired,
};

export default Offer;
